import { assertAllowedCellId, assertSafePathSegment } from '@/lib/safe-outbound-url';
import { getTokenInfo } from './functions';

/**
 * Resolve ``custom:cell_id`` from a JWT (ID token). Returns ``null`` when the
 * token is missing/unparseable or when the claim is not present.
 *
 * Shared by every direct-CORS client that targets a per-cell API gateway
 * (provenance-service, junkdrawer-service, …). Hooks gate their queries on
 * the value already in ``AuthContext`` so we don't have to wait for Amplify
 * to resolve a fresh session.
 */
export function resolveCellIdFromIdToken(idToken: string | null | undefined): string | null {
  if (!idToken) return null;
  try {
    const info = getTokenInfo(idToken);
    const cellId =
      (info?.['custom:cell_id'] as string | undefined) ||
      (info?.payload?.['custom:cell_id'] as string | undefined) ||
      null;
    return cellId && cellId.trim() !== '' ? cellId : null;
  } catch {
    return null;
  }
}

/** Suffix for the cell API host. Commercial / govcloud default — do not change. */
export const CELL_API_DOMAIN_SUFFIX = 'api.tranquility.ai';

/**
 * Suffix for the cell **frontend/app** host (``{cellId}.timepilot.tranquility.ai``).
 *
 * Distinct from {@link CELL_API_DOMAIN_SUFFIX}: the app host is served by the
 * cell frontend ALB (forward-only, no edge bearer-token authorizer), so a
 * browser navigation — e.g. a cross-origin third-party-integration ``<form>``
 * POST — reaches the per-cell path rules (``/evidence-com/*`` …) directly. The
 * ``.api`` host sits behind the API Gateway authorizer, which rejects
 * header-less browser POSTs with 401. Mirrors ``get_edge_app_domain`` /
 * ``AMPLIFY_APP_ORIGIN`` on the infra side.
 */
export const CELL_APP_DOMAIN_SUFFIX = 'timepilot.tranquility.ai';

/**
 * Knox edge infix from ``PartitionSpec.edge_domain_infix``. Only this partition
 * uses a distinct public DNS tree (``*.knox.{root}``). Govcloud stays on the
 * commercial ``api.tranquility.ai`` suffix used by the CORS clients today.
 *
 * Knox serves every root in ``get_root_domains("knox")`` side by side; a root
 * missing here makes its hosts read as commercial (wrong API suffix, and the
 * 20-minute idle policy instead of Knox's 15).
 */
const KNOX_ROOT_RE =
  /(?:^|\.)knox\.(tranquility\.ai|tranquility-ai2\.com|tranquilitysi\.com|tranquility-ai-sandbox\.com)$/i;

function normalizeHostname(hostOrUrl: string): string {
  return hostOrUrl.trim().replace(/^https?:\/\//i, '').split('/')[0]?.split(':')[0] ?? '';
}

/** Root domain after the ``knox.`` infix, or ``null`` when the host is not Knox. */
export function knoxRootFromHostname(hostOrUrl: string): string | null {
  const match = normalizeHostname(hostOrUrl).match(KNOX_ROOT_RE);
  return match ? match[1].toLowerCase() : null;
}

/**
 * ``url`` moved onto the Knox root of ``arrivalHostOrUrl``, when both are Knox
 * hosts on different roots; otherwise ``url`` unchanged.
 *
 * Knox serves every cell on each of its roots, but server-built links (e.g. the
 * edge cell-switcher's redirect) name one root. This keeps a user on the root
 * they arrived on. Only a recognised Knox root is ever substituted, so the
 * result cannot point off-platform.
 */
export function withKnoxRootOf(url: string, arrivalHostOrUrl: string): string {
  const arrivalRoot = knoxRootFromHostname(arrivalHostOrUrl);
  if (!arrivalRoot) return url;
  let parsed: URL;
  try {
    parsed = new URL(url);
  } catch {
    return url;
  }
  const root = knoxRootFromHostname(parsed.hostname);
  if (!root || root === arrivalRoot) return url;
  parsed.hostname = `${parsed.hostname.slice(0, -root.length)}${arrivalRoot}`;
  return parsed.toString();
}

function runtimeHostname(): string {
  if (typeof window !== 'undefined' && window.location?.hostname) {
    return window.location.hostname;
  }
  const origin = process.env.AMPLIFY_APP_ORIGIN;
  if (origin) {
    try {
      return new URL(origin.includes('://') ? origin : `https://${origin}`).hostname;
    } catch {
      return normalizeHostname(origin);
    }
  }
  return process.env.API_DOMAIN_SUFFIX ?? '';
}

/**
 * API host suffix for CORS / gateway calls.
 *
 * Knox is the only partition that switches: ``api.knox.{root}``. Commercial and
 * govcloud keep {@link CELL_API_DOMAIN_SUFFIX}. ``hostname`` is a test seam;
 * production callers omit it and we read the page host (or server env).
 */
export function resolveCellApiDomainSuffix(hostname?: string): string {
  const knoxRoot = knoxRootFromHostname(hostname ?? runtimeHostname());
  return knoxRoot ? `api.knox.${knoxRoot}` : CELL_API_DOMAIN_SUFFIX;
}

/**
 * App host suffix for cell-frontend navigations. Knox-only switch, same rule
 * as {@link resolveCellApiDomainSuffix}.
 */
export function resolveCellAppDomainSuffix(hostname?: string): string {
  const knoxRoot = knoxRootFromHostname(hostname ?? runtimeHostname());
  return knoxRoot ? `timepilot.knox.${knoxRoot}` : CELL_APP_DOMAIN_SUFFIX;
}

/**
 * Build a fully-qualified cell-API URL given a service-specific path prefix
 * (e.g. ``/junkdrawer-service/v1``). ``subPath`` is the route under that
 * prefix; a leading slash is optional.
 */
export function buildCellServiceUrl(
  cellId: string,
  servicePrefix: string,
  subPath: string,
): string {
  assertAllowedCellId(cellId);
  assertSafePathSegment(servicePrefix, 'service prefix');
  assertSafePathSegment(subPath, 'service path');
  const prefix = servicePrefix.startsWith('/') ? servicePrefix : `/${servicePrefix}`;
  const sub = subPath.startsWith('/') ? subPath : `/${subPath}`;
  return `https://${cellId.trim()}.${resolveCellApiDomainSuffix()}${prefix}${sub}`;
}

/** Primitive value types accepted by direct-CORS query maps. */
export type QueryValue = string | number | boolean | null | undefined;

/** Query map: scalar values are emitted once; arrays are emitted as repeated keys. */
export type CellServiceQuery = Record<string, QueryValue | QueryValue[]>;

/** Append a ``CellServiceQuery`` map to ``url`` as standard URL query params. */
export function appendQuery(url: string, query: CellServiceQuery | undefined): string {
  if (!query) return url;
  const parts: string[] = [];
  for (const [key, value] of Object.entries(query)) {
    if (value === undefined || value === null) continue;
    if (Array.isArray(value)) {
      for (const v of value) {
        if (v === undefined || v === null) continue;
        parts.push(`${encodeURIComponent(key)}=${encodeURIComponent(String(v))}`);
      }
    } else {
      parts.push(`${encodeURIComponent(key)}=${encodeURIComponent(String(value))}`);
    }
  }
  const qs = parts.join('&');
  return qs ? `${url}?${qs}` : url;
}
